import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'dva'

import InsiderPlusIcon from '../../../assets/svg/InsiderPlus'
import X from '../../../assets/svg/Feather/X'
import ghAnalytics from '../../../lib/analytics'
import { USER_ROLES } from '../../../lib/constants'
import { getIsRoleUpgradeable, getIsStaffMember } from '../../../lib/utils'
import useOnClickOutside from '../../../lib/hooks/useOnClickOutside'
import styles from './ToolBarInsiderPlusNav.module.scss'

export const MAPS_NAV_INSIDER_PLUS_POPUP_SEEN_KEY = 'mapsNavInsiderPlusPopupOpened'

function useInsiderPlusNavEligible(userProfile) {
  return useMemo(() => {
    const roles = userProfile?.roles
    if (!roles?.length) return false
    const userRole = roles[0] || USER_ROLES.guest
    const normalized = userRole ? String(userRole).toLowerCase() : USER_ROLES.guest
    return getIsRoleUpgradeable(USER_ROLES.insider_plus, normalized) && !getIsStaffMember(normalized)
  }, [userProfile?.roles])
}

function ToolBarInsiderPlusNav(props) {
  const { cmsBaseUrl, sidebarView, userProfile, popupOpen, onPopupOpenChange, hideSidebar } = props
  const containerRef = useRef(null)
  const prevSidebarViewRef = useRef()
  const [isHovered, setIsHovered] = useState(false)
  const [showUnreadDot, setShowUnreadDot] = useState(() => {
    try {
      return !localStorage.getItem(MAPS_NAV_INSIDER_PLUS_POPUP_SEEN_KEY)
    } catch {
      return true
    }
  })

  const eligible = useInsiderPlusNavEligible(userProfile)
  const cmsRoot = cmsBaseUrl || 'https://www.gohunt.com'
  const upgradeHref = `${cmsRoot}/user/register?plan=insider-plus`
  const learnHref = `${cmsRoot}/learn/insider-plus`

  const markPopupOpened = useCallback(() => {
    try {
      localStorage.setItem(MAPS_NAV_INSIDER_PLUS_POPUP_SEEN_KEY, '1')
    } catch {
      /* ignore quota / private mode */
    }
    setShowUnreadDot(false)
  }, [])

  useEffect(() => {
    const prev = prevSidebarViewRef.current
    if (prev !== undefined && prev !== sidebarView.view) {
      onPopupOpenChange(false)
    }
    prevSidebarViewRef.current = sidebarView.view
  }, [sidebarView.view, onPopupOpenChange])

  const handleClickOutside = useCallback(() => {
    if (popupOpen) onPopupOpenChange(false)
  }, [popupOpen, onPopupOpenChange])

  useOnClickOutside(containerRef, handleClickOutside)

  const togglePopup = () => {
    if (popupOpen) {
      onPopupOpenChange(false)
    } else {
      markPopupOpened()
      hideSidebar()
      onPopupOpenChange(true)
    }
  }

  const logNavCta = (ctaDestination) => {
    ghAnalytics.logMapsNavUpgradeCta({ cta_destination: ctaDestination })
  }

  const iconColor = () => {
    if (popupOpen || isHovered) return '#fff'
    return '#8e8e92'
  }

  if (!eligible) return null

  return (
    <div ref={containerRef} className={styles.root}>
      <div className={styles.toolbarButtonContainer}>
        <button
          type="button"
          className={`${styles.toolbarButton} ${popupOpen ? styles.activePopup : ''}`}
          aria-expanded={popupOpen}
          aria-haspopup="dialog"
          aria-label="Insider Plus"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={togglePopup}
        >
          {showUnreadDot ? <span className={styles.unreadDot} aria-hidden /> : null}
          <InsiderPlusIcon className={styles.buttonIcon} width={20} height={20} color={iconColor()} />
        </button>
        <span className={styles.tooltip}>Insider+</span>
      </div>
      {popupOpen ? (
        <div className={styles.popup} role="dialog" aria-labelledby="maps-insider-plus-headline">
          <button type="button" className={styles.popupClose} onClick={() => onPopupOpenChange(false)} aria-label="Close">
            <X width={12} color="#FAFAFA" />
          </button>
          <div className={styles.popupContent}>
            <h2 id="maps-insider-plus-headline" className={styles.headline}>
              Need some help <br/>with e-scouting?
            </h2>
            <p className={styles.body}>
              GOHUNT offers hunt consulting, e-scouting guidance—hell, we&apos;ll even do your applications.
            </p>
          </div>
          <div className={styles.actions}>
            <a
              className={styles.btnUpgrade}
              href={upgradeHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => logNavCta('register')}
            >
              Upgrade
            </a>
            <a
              className={styles.btnLearn}
              href={learnHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => logNavCta('learn_more')}
            >
              Learn more
            </a>
          </div>
        </div>
      ) : null}
    </div>
  )
}

ToolBarInsiderPlusNav.propTypes = {
  cmsBaseUrl: PropTypes.string,
  sidebarView: PropTypes.object,
  userProfile: PropTypes.object,
  popupOpen: PropTypes.bool,
  onPopupOpenChange: PropTypes.func,
  hideSidebar: PropTypes.func,
}

ToolBarInsiderPlusNav.defaultProps = {
  cmsBaseUrl: '',
  sidebarView: {},
  userProfile: {},
  popupOpen: false,
  onPopupOpenChange: () => {},
  hideSidebar: () => {},
}

export default connect(
  model => ({
    cmsBaseUrl: model.app.config?.REACT_APP_GOHUNT_CMS_URL || '',
    sidebarView: model.app.sidebarView,
    userProfile: model.user.profile,
  }),
  dispatch => ({
    hideSidebar: () => dispatch({ type: 'app/setShowSidebar', payload: false }),
  })
)(ToolBarInsiderPlusNav)
